import { defaultPack, type ContentPack } from "./content/defaultPack";
import type { BootstrapData, LessonProgress, UserSettings } from "./types";

const SETTINGS_KEY = "edge-english:settings";
const PROGRESS_KEY = "edge-english:progress";

const defaultSettings: UserSettings = {
  dailyMinutes: 12,
  activePhase: "foundation",
};

const defaultProgress: LessonProgress = {
  lessonId: defaultPack.lessons[0].id,
  completedSteps: 0,
  totalSteps: 3,
  streak: 0,
  flareSeeds: 0,
};

function readLocal<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export async function loadBootstrap(): Promise<BootstrapData> {
  try {
    const remote = await request<BootstrapData>("/api/bootstrap");
    if (remote.user.email === "Local device mode") {
      // Anonymous mode: this device is the source of truth for settings and progress.
      return {
        ...remote,
        settings: readLocal(SETTINGS_KEY, remote.settings),
        progress: readLocal(PROGRESS_KEY, remote.progress),
      };
    }
    return remote;
  } catch {
    return {
      user: { email: "Local preview" },
      settings: readLocal(SETTINGS_KEY, defaultSettings),
      progress: readLocal(PROGRESS_KEY, defaultProgress),
      content: defaultPack,
    };
  }
}

export async function saveSettings(settings: UserSettings): Promise<UserSettings> {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  try {
    return await request<UserSettings>("/api/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  } catch {
    return settings;
  }
}

export async function saveProgress(progress: LessonProgress): Promise<LessonProgress> {
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  try {
    return await request<LessonProgress>("/api/progress", {
      method: "PUT",
      body: JSON.stringify(progress),
    });
  } catch {
    return progress;
  }
}

export async function replaceContentPack(content: ContentPack): Promise<ContentPack> {
  return request<ContentPack>("/api/content", {
    method: "PUT",
    body: JSON.stringify(content),
  });
}
